/**
 * Host Persona API service for admin interface
 * Handles CRUD operations for host personas
 */

import type {
  CreateHostPersonaRequest,
  HostPersona,
  UpdateHostPersonaRequest,
} from '@musedrops/shared';
import {
  createHostPersona as createHostPersonaRecord,
  deleteHostPersona as deleteHostPersonaRecord,
  getAllHostPersonas,
  getHostPersona as fetchHostPersona,
  listHostPersonas,
  updateHostPersona as updateHostPersonaRecord,
} from '../data/host-persona';
import { supabase } from './supabase';

export class HostPersonaApi {
  static async getAll(): Promise<HostPersona[]> {
    const { data } = await listHostPersonas();
    return data;
  }

  static async getById(id: string): Promise<HostPersona> {
    return fetchHostPersona(id);
  }

  static async create(
    personaData: CreateHostPersonaRequest
  ): Promise<HostPersona> {
    return createHostPersonaRecord(personaData);
  }

  static async update(
    id: string,
    personaData: UpdateHostPersonaRequest
  ): Promise<HostPersona> {
    return updateHostPersonaRecord(id, personaData);
  }

  static async delete(id: string): Promise<void> {
    // Check if persona is used by any shows
    const { data: showsWithPersona1, error: error1 } = await supabase
      .from('shows')
      .select('id')
      .eq('host_persona_1', (await fetchHostPersona(id)).key)
      .limit(1);

    if (error1) {
      throw new Error(`Failed to check persona usage: ${error1.message}`);
    }

    const { data: showsWithPersona2, error: error2 } = await supabase
      .from('shows')
      .select('id')
      .eq('host_persona_2', (await fetchHostPersona(id)).key)
      .limit(1);

    if (error2) {
      throw new Error(`Failed to check persona usage: ${error2.message}`);
    }

    const hasAssociatedShows =
      (showsWithPersona1 && showsWithPersona1.length > 0) ||
      (showsWithPersona2 && showsWithPersona2.length > 0);

    if (hasAssociatedShows) {
      throw new Error(
        'Cannot delete host persona: There are shows using this persona'
      );
    }

    await deleteHostPersonaRecord(id);
  }

  /**
   * Format personas for use in prompt templates
   * Returns formatted string: "- key: good_for\n- key2: good_for2\n..."
   *
   * Example:
   * - skeptical-friend: Explainer, Mystery, News Analysis, Review; works as S2 in teller_sidekick or S1 in interview; fits shows that benefit from a questioning voice or listener stand-in.
   * - curious-enthusiast: Explainer, Profile, True Story, Comedy; works as S2 in teller_sidekick or setup_payoff; fits shows that want infectious energy and a sense of discovery.
   */
  static async formatForPrompt(): Promise<string> {
    const personas = await HostPersonaApi.getAll();

    if (personas.length === 0) {
      return '(No personas available)';
    }

    return personas.map(p => `- ${p.key}: ${p.goodFor}`).join('\n');
  }
}

export const getHostPersonas = getAllHostPersonas;
